import React, { useEffect, useState, useCallback } from 'react';
import NavBar from '../../components/NavBar';
import '../../../pages/FNOL/fonts.css';
import './Dashboard.css';
import FNOLProcessedChart from '../../components/FNOLProcessedChart';
import FNOLPendingActivityChart from '../../components/FNOLPendingActivityChart';
import FNOLLifecycleChart from '../../components/FNOLLifecycleChart';
import AgingBucketChart from '../../components/AgingBucketChart';
import TriagingDashboardChart from '../../components/TriagingDashboardChart';
import AssignmentAnalysisChart from '../../components/AssignmentAnalysisChart';
import FilterBar from '../../components/FilterBar';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '../../../api';
import { getSessionName } from '../../../services/authService';

const DASHBOARD_INBOX_FILTERS = {
    NEW: 'new',
    OPEN: 'open',
    PROCESSED: 'processed'
};

const isSuccessfulResponse = (response) => {
    return response?.status === 'success' || response?.success === true;
};

const unwrapResponseData = (response) => {
    if (!response || typeof response !== 'object') return null;
    if (Object.prototype.hasOwnProperty.call(response, 'data')) return response.data;
    return response;
};

const hasUsableResponsePayload = (response) => {
    const payload = unwrapResponseData(response);
    if (!payload) return false;

    if (response && Object.prototype.hasOwnProperty.call(response, 'status') && response.status !== 'success') {
        return false;
    }

    if (response?.success === false) {
        return false;
    }

    return true;
};

const firstNumericValue = (...values) => {
    for (const value of values) {
        const numeric = Number(value);
        if (Number.isFinite(numeric)) return numeric;
    }
    return 0;
};

const hasActiveDashboardFilters = (appliedFilters = {}) => {
    return Boolean(
        appliedFilters.date_from ||
        appliedFilters.date_to ||
        (appliedFilters.location && appliedFilters.location !== 'All') ||
        (appliedFilters.global_event && appliedFilters.global_event !== 'All') ||
        (appliedFilters.estimated_loss_range && appliedFilters.estimated_loss_range !== 'All')
    );
};

const normalizeDashboardStats = (statisticsResponse) => {
    const payload = unwrapResponseData(statisticsResponse) || {};

    return {
        total: firstNumericValue(
            payload.total_fnols,
            payload.total_fnol,
            payload.total_count,
            payload.total,
            payload.fnol_count
        ),
        new: firstNumericValue(
            payload.new_fnols,
            payload.new,
            payload.new_count
        ),
        open: firstNumericValue(
            payload.open_fnols,
            payload.open,
            payload.open_count,
            payload.in_progress_fnols
        ),
        processed: firstNumericValue(
            payload.processed_fnols,
            payload.processed,
            payload.processed_count,
            payload.completed_fnols,
            payload.closed_fnols
        )
    };
};

const Dashboard = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        total: 0,
        new: 0,
        open: 0,
        processed: 0
    });
    const [distributionData, setDistributionData] = useState(null);
    const [pendingActivityData, setPendingActivityData] = useState(null);
    const [lifecycleData, setLifecycleData] = useState(null);
    const [agingBucketsData, setAgingBucketsData] = useState(null);
    const [triagingData, setTriagingData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [userName, setUserName] = useState("");
    const [filters, setFilters] = useState({
        date_from: '',
        date_to: '',
        location: 'All',
        global_event: 'All',
        estimated_loss_range: 'All'
    });

    const fetchDashboardData = useCallback(async (appliedFilters = filters) => {
        setLoading(true);
        try {
            const hasActiveFilters = hasActiveDashboardFilters(appliedFilters);

            // Fetch all dashboard data in parallel
            const [
                statisticsResponse,
                distributionResponse,
                pendingActivityResponse,
                lifecycleResponse,
                agingBucketsResponse,
                fallbackTotalCount,
            ] = await Promise.all([
                dashboardAPI.getStatistics(appliedFilters),
                dashboardAPI.getFNOLDistribution(appliedFilters),
                dashboardAPI.getPendingActivity(appliedFilters),
                dashboardAPI.getLifecycleTimeline({
                    period: '6months',
                    granularity: 'monthly',
                    ...appliedFilters
                }),
                dashboardAPI.getAgingBuckets(appliedFilters),
                hasActiveFilters ? Promise.resolve(null) : dashboardAPI.getFNOLCount(),
            ]);

            // Set statistics
            const parsedStats = normalizeDashboardStats(statisticsResponse);
            const resolvedTotal = (!hasActiveFilters && Number.isFinite(Number(fallbackTotalCount)))
                ? Number(fallbackTotalCount)
                : parsedStats.total;

            setStats({
                total: resolvedTotal,
                new: parsedStats.new,
                open: parsedStats.open,
                processed: parsedStats.processed
            });

            // Set distribution data
            if (isSuccessfulResponse(distributionResponse) || hasUsableResponsePayload(distributionResponse)) {
                setDistributionData(unwrapResponseData(distributionResponse));
            }

            // Set pending activity data
            if (isSuccessfulResponse(pendingActivityResponse) || hasUsableResponsePayload(pendingActivityResponse)) {
                setPendingActivityData(unwrapResponseData(pendingActivityResponse));
            }

            // Set lifecycle data
            if (isSuccessfulResponse(lifecycleResponse) || hasUsableResponsePayload(lifecycleResponse)) {
                setLifecycleData(unwrapResponseData(lifecycleResponse));
            }

            // Set aging buckets data
            if (isSuccessfulResponse(agingBucketsResponse) || hasUsableResponsePayload(agingBucketsResponse)) {
                setAgingBucketsData(unwrapResponseData(agingBucketsResponse));
            }

        } catch (error) {
            console.error("Failed to fetch dashboard data:", error);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchDashboardData();
        getSessionName().then(setUserName);
    }, []);

    const handleFilterApply = (newFilters) => {
        setFilters(newFilters);
        fetchDashboardData(newFilters);
    };

    const handleInboxCardNavigation = useCallback((statusFilter) => {
        navigate('/', {
            state: {
                inboxStatusFilter: statusFilter
            }
        });
    }, [navigate]);

    // ── Capacity & Alert Computations ────────────────────────────────────────
    const MAX_FNOLS_PER_HANDLER = 14;
    const CAPACITY_THRESHOLD = 78.6;
    const RADIUS = 34;
    const CIRCUMFERENCE = 2 * Math.PI * RADIUS; // ≈ 213.6

    const activeFNOLs = stats.new + stats.open;
    const capacityRaw = activeFNOLs > 0 ? (activeFNOLs / MAX_FNOLS_PER_HANDLER) * 100 : 0;
    const capacityPercent = Math.round(capacityRaw * 10) / 10;
    const cappedPct = Math.min(capacityPercent, 100);
    const dashOffset = CIRCUMFERENCE * (1 - cappedPct / 100);

    const capacityStatus = (() => {
        if (Math.abs(capacityPercent - CAPACITY_THRESHOLD) < 0.05) return "High";
        if (capacityPercent > CAPACITY_THRESHOLD) return "Overloaded";
        return "Normal";
    })();

    const STATUS_CONFIG = {
        Normal: { color: "#22c55e", label: "Normal", message: "You are almost done. Keep it up!" },
        High: { color: "#f59e0b", label: "High", message: "Approaching capacity. Stay alert!" },
        Overloaded: { color: "#ef4444", label: "Overloaded", message: "Handler is overloaded. Prioritize!" },
    };
    const { color: statusColor, label: statusLabel, message: statusMessage } = STATUS_CONFIG[capacityStatus];

    const overdueCount = agingBucketsData?.buckets?.find(b => b.bucket_id === "15+")?.count || 0;
    const slaBreachCount = lifecycleData?.summary?.total_sla_breaches || 0;
    const totalAlerts = overdueCount + slaBreachCount;
    // ─────────────────────────────────────────────────────────────────────────

    return (
        <div className="dashboard-container font-sans min-h-screen w-full flex flex-col bg-[#E7F3FF] overflow-x-hidden">
            <NavBar />

            <div className="w-full px-4 sm:px-6 lg:px-8 py-2">
                {/* ── Welcome / Alert / Capacity Header Card ── */}
                <div className="bg-white rounded-2xl px-6 py-3 shadow-sm mb-2 flex flex-col md:flex-row justify-between items-center gap-3">

                    {/* LEFT: Welcome greeting + Attention Required section */}
                    <div className="flex items-start gap-4 w-full md:w-auto">
                        <div>
                            <h1 className="text-2xl font-bold text-[#006BB3] mb-3">Welcome, {userName || 'User'}!</h1>

                            {/* Attention Required */}
                            {loading ? (
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 bg-gray-200 rounded-full animate-pulse" />
                                    <div className="space-y-1.5">
                                        <div className="w-28 h-3 bg-gray-200 rounded animate-pulse" />
                                        <div className="w-20 h-3 bg-gray-200 rounded animate-pulse" />
                                    </div>
                                </div>
                            ) : totalAlerts > 0 ? (
                                <div className="flex items-start gap-3">
                                    {/* Warning icon */}
                                    <svg width="38" height="38" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
                                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" fill="#ef4444" />
                                        <path d="M12 9v4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M12 17h.01" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <div>
                                        <p className="font-bold text-[#1F2937] text-sm leading-tight">
                                            <span className="text-red-600">{totalAlerts} FNOL{totalAlerts > 1 ? "s" : ""}</span> Require{totalAlerts === 1 ? "s" : ""} Attention
                                        </p>
                                        <div className="flex gap-2 mt-1.5 flex-wrap">
                                            {slaBreachCount > 0 && (
                                                <span className="inline-flex items-center gap-1 bg-red-50 text-red-700 text-xs font-semibold px-2 py-0.5 rounded-full border border-red-200">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 inline-block" />
                                                    {slaBreachCount} SLA Breach{slaBreachCount > 1 ? "es" : ""}
                                                </span>
                                            )}
                                            {overdueCount > 0 && (
                                                <span className="inline-flex items-center gap-1 bg-orange-50 text-orange-700 text-xs font-semibold px-2 py-0.5 rounded-full border border-orange-200">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-orange-500 inline-block" />
                                                    {overdueCount} Overdue (15+ days)
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2">
                                    {/* Checkmark icon */}
                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M22 4L12 14.01l-3-3" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <p className="font-semibold text-green-600 text-sm">All FNOLs on track</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* RIGHT: Capacity Used section */}
                    <div className="flex items-center gap-5 w-full md:w-auto justify-between md:justify-end">
                        <div className="text-right hidden md:block">
                            <p className="text-[#1F2937] font-bold text-sm mb-1">
                                {loading ? "Loading capacity…" : statusMessage}
                            </p>
                            <div className="flex items-center justify-end gap-1.5 mt-1">
                                <span className="text-xs text-gray-500 font-medium">Capacity:</span>
                                {loading ? (
                                    <div className="w-16 h-4 bg-gray-200 rounded-full animate-pulse" />
                                ) : (
                                    <span
                                        className="text-xs font-bold px-2.5 py-0.5 rounded-full"
                                        style={{ backgroundColor: `${statusColor}22`, color: statusColor, border: `1px solid ${statusColor}55` }}
                                    >
                                        {statusLabel}
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-gray-400 mt-1">
                                {loading ? "" : `${activeFNOLs} / ${MAX_FNOLS_PER_HANDLER} active FNOLs`}
                            </p>
                        </div>

                        {/* Circular Progress Indicator */}
                        <div className="relative w-24 h-24 flex-shrink-0">
                            <svg className="w-full h-full transform -rotate-90 block" viewBox="0 0 80 80">
                                {/* Track ring */}
                                <circle
                                    cx="40" cy="40" r={RADIUS}
                                    fill="none"
                                    stroke={loading ? "#e5e7eb" : `${statusColor}33`}
                                    strokeWidth="10"
                                />
                                {/* Progress arc */}
                                <circle
                                    cx="40" cy="40" r={RADIUS}
                                    fill="none"
                                    stroke={loading ? "#d1d5db" : statusColor}
                                    strokeWidth="10"
                                    strokeDasharray={CIRCUMFERENCE}
                                    strokeDashoffset={loading ? CIRCUMFERENCE * 0.7 : dashOffset}
                                    strokeLinecap="round"
                                    style={{ transition: "stroke-dashoffset 0.6s ease, stroke 0.4s ease" }}
                                />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <span
                                    className="font-bold text-lg leading-none"
                                    style={{ color: loading ? "#9ca3af" : statusColor }}
                                >
                                    {loading ? "—" : `${capacityPercent}%`}
                                </span>
                                <span className="text-[9px] text-gray-400 leading-none mt-0.5 font-medium">Used</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filter Bar Container */}
                <div className="bg-white rounded-2xl p-3 shadow-sm mb-2">
                    <FilterBar onFilterApply={handleFilterApply} />
                </div>

                {/* Stats Grid Container */}
                <div className="bg-white rounded-2xl p-3 shadow-sm mb-2">
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                        <StatCard
                            title="Total FNOLs"
                            value={loading ? "-" : stats.total}
                        />
                        <StatCard
                            title="New"
                            value={loading ? "-" : stats.new}
                            onClick={() => handleInboxCardNavigation(DASHBOARD_INBOX_FILTERS.NEW)}
                        />
                        <StatCard
                            title="Open"
                            value={loading ? "-" : stats.open}
                            onClick={() => handleInboxCardNavigation(DASHBOARD_INBOX_FILTERS.OPEN)}
                        />
                        <StatCard
                            title="Processed"
                            value={loading ? "-" : stats.processed}
                            onClick={() => handleInboxCardNavigation(DASHBOARD_INBOX_FILTERS.PROCESSED)}
                        />
                        <StatCard
                            title="Average Handling Time"
                            value={loading ? "-" : "- - h"}
                        />
                    </div>
                </div>


                {/* Charts Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 mb-2">
                    <div className="bg-white rounded-2xl p-3 shadow-sm">
                        <FNOLProcessedChart
                            stats={stats}
                            distributionData={distributionData}
                            loading={loading}
                        />
                    </div>
                    <div className="bg-white rounded-2xl p-3 shadow-sm">
                        <FNOLPendingActivityChart
                            data={pendingActivityData}
                            loading={loading}
                        />
                    </div>
                </div>

                {/* Second Charts Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 mb-2">
                    <div className="bg-white rounded-2xl p-3 shadow-sm">
                        <FNOLLifecycleChart
                            data={lifecycleData}
                            loading={loading}
                        />
                    </div>
                    <div className="bg-white rounded-2xl p-3 shadow-sm">
                        <AgingBucketChart
                            data={agingBucketsData}
                            loading={loading}
                        />
                    </div>
                </div>

                {/* Third Charts Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 mb-2">
                    <div className="bg-white rounded-2xl p-3 shadow-sm">
                        <TriagingDashboardChart />
                    </div>
                    <div className="bg-white rounded-2xl p-3 shadow-sm">
                        <AssignmentAnalysisChart />
                    </div>
                </div>
            </div>
        </div>
    );
};

const StatCard = ({ title, value, onClick }) => {
    const isClickable = typeof onClick === 'function';

    if (isClickable) {
        return (
            <button
                type="button"
                onClick={onClick}
                className="bg-[#eaf4ff] px-3 py-2 rounded-xl shadow-sm hover:shadow-md transition-shadow h-full flex flex-col justify-between text-left cursor-pointer"
            >
                <p className="text-gray-700 font-medium text-sm mb-1">{title}</p>
                <p className="text-3xl font-bold text-gray-900">{value}</p>
            </button>
        );
    }

    return (
        <div className="bg-[#eaf4ff] px-3 py-2 rounded-xl shadow-sm hover:shadow-md transition-shadow h-full flex flex-col justify-between">
            <p className="text-gray-700 font-medium text-sm mb-1">{title}</p>
            <p className="text-3xl font-bold text-gray-900">{value}</p>
        </div>
    );
};

export default Dashboard;